/**
 * Topological projection order (RFC 0215: Log substrate).
 *
 * The log is a DAG; the LLM API and the object view both need a linear order.
 * This is a true topological sort over the `parents` DAG, with (createdAt, id)
 * only as a tie-break among concurrent siblings, replacing Searugast's
 * prototype (createdAt, id) ordering which is not skew-safe.
 */

import type { Entry, EntryId, LogDoc } from './types'

/** Stable comparator over entry ids: (createdAt, id). */
function compareIds(doc: LogDoc, a: EntryId, b: EntryId): number {
  const ea = doc.entries[a]
  const eb = doc.entries[b]
  if (ea.createdAt !== eb.createdAt) return ea.createdAt < eb.createdAt ? -1 : 1
  return a < b ? -1 : a > b ? 1 : 0
}

/**
 * Entries in topological order (Kahn's algorithm, deterministic tie-break).
 *
 * A DOC WITH NO `entries` IS EMPTY, NOT BROKEN. `repo.find` on a document this
 * peer has never seen answers with a doc that has no fields at all, which is the
 * "absent is not empty" shape, and `Object.values(undefined)` throws on it. That
 * throw reached `readHoldingsPrompt`, whose catch turns any failure into '', so
 * an unsynced session log looked exactly like a session holding nothing and the
 * agent's holdings block vanished with nothing said about why.
 */
/**
 * Entries in topological order (Kahn's algorithm, deterministic tie-break).
 *
 * A DOC WITH NO `entries` IS EMPTY, NOT BROKEN. `repo.find` on a document this
 * peer has never seen answers with a doc that has no fields at all, which is the
 * "absent is not empty" shape, and `Object.values(undefined)` throws on it. That
 * throw reached `readHoldingsPrompt`, whose catch turns any failure into '', so
 * an unsynced session log looked exactly like a session holding nothing and the
 * agent's holdings block vanished with nothing said about why.
 *
 * REMEMBERED PER DOCUMENT OBJECT. A decoded log and an Automerge document are
 * both replaced, never mutated, when the log moves on, so the object is the
 * version and remembering the order against it is exact. Sixteen projections
 * call this, several of them once per run entry from a daemon timer, and a
 * profile of a daemon pinned at a core put this function at 27% of self time
 * for an answer that never changed between calls. The contract this relies
 * on: a `LogDoc` handed to a projection is not edited in place afterwards.
 * Build a new object instead; the whole suite runs against that rule.
 */
const orders = new WeakMap<object, Entry[]>()

export function topoOrder(doc: LogDoc): Entry[] {
  if (!doc?.entries) return []
  // Only a frozen `entries` is trusted to stand for its version: a decoded
  // log is frozen by the codec, while a plain object a test builds up in place
  // is not, and simply pays for the sort each time.
  if (!Object.isFrozen(doc.entries)) return sortTopologically(doc)
  const key = doc as unknown as object
  const had = orders.get(key)
  if (had) return had
  const out = sortTopologically(doc)
  orders.set(key, out)
  return out
}

function sortTopologically(doc: LogDoc): Entry[] {
  const all = Object.values(doc.entries)
  // Timestamps read once into a map: the comparator runs O(n log n) times and
  // need not touch the entry record each time.
  const at = new Map<EntryId, number>()
  for (const e of all) at.set(e.id, e.createdAt)
  const cmp = (a: EntryId, b: EntryId): number => {
    const ta = at.get(a) ?? 0
    const tb = at.get(b) ?? 0
    if (ta !== tb) return ta < tb ? -1 : 1
    return a < b ? -1 : a > b ? 1 : 0
  }
  const indegree = new Map<EntryId, number>()
  const childrenOf = new Map<EntryId, EntryId[]>()
  for (const e of all) indegree.set(e.id, 0)
  for (const e of all) {
    for (const p of e.parents) {
      if (!doc.entries[p]) continue // parent outside this doc: ignore
      indegree.set(e.id, (indegree.get(e.id) ?? 0) + 1)
      const arr = childrenOf.get(p) ?? []
      arr.push(e.id)
      childrenOf.set(p, arr)
    }
  }
  // Kahn's algorithm with a sorted ready list kept sorted by insertion, rather
  // than re-sorted on every arrival, and consumed by index rather than shift.
  const ready = all
    .filter((e) => (indegree.get(e.id) ?? 0) === 0)
    .map((e) => e.id)
    .sort(cmp)
  const insert = (id: EntryId, from: number): void => {
    let lo = from
    let hi = ready.length
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (cmp(ready[mid], id) < 0) lo = mid + 1
      else hi = mid
    }
    ready.splice(lo, 0, id)
  }
  const out: Entry[] = []
  for (let i = 0; i < ready.length; i++) {
    const id = ready[i]
    out.push(doc.entries[id])
    for (const c of childrenOf.get(id) ?? []) {
      const next = (indegree.get(c) ?? 0) - 1
      indegree.set(c, next)
      if (next === 0) insert(c, i + 1)
    }
  }
  return out
}
